import React, { useEffect, useState } from "react";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import { notification } from "antd";
import Loader from "@appComponents/Loader/Loader";
import { MyOrdersList } from "../components/MyOrdersList/MyOrdersList";
import { IOrder } from "@market/interfaces/IOrder";
import moment from "moment";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { EordersTypes } from "@market/enums/OrdersTypes";
import SelectV2 from "@appComponents/SelectV2";

const typeOptions = [
  {
    value: "all",
    label: "All",
  },
  {
    value: EordersTypes.buy,
    label: "Buy",
  },
  {
    value: EordersTypes.sell,
    label: "Sell",
  },
  {
    value: EordersTypes.suspended,
    label: "Suspended",
  },
];

const defaultDatePickerProps: Omit<ReactDatePickerProps, "onChange"> = {
  className: "tailwind-datepicker",
  withPortal: true,
  showYearDropdown: true,
  showMonthDropdown: true,
  dateFormat: "d MMM yyyy",
  isClearable: true,
};

export const MyOrdersTab = () => {
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [typeFilter, setTypeFilter] = useState(typeOptions[0].value);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [fromDateFilter, setFromDateFilter] = useState<Date | undefined>();
  const [toDateFilter, setToDateFilter] = useState<Date | undefined>();

  const refetchOrders = () => {
    setOrdersLoading(true);

    SecondaryMarketService.getMyOrders()
      .then((resp) => {
        setOrdersLoading(false);
        setOrders(
          resp.data.filter((order: IOrder) => order.status !== "cancelled")
        );
      })
      .catch(() => {
        setOrdersLoading(false);
        notification.error({ message: "Something went wrong." });
      });
  };

  useEffect(() => {
    SecondaryMarketService.getMyOrders()
      .then((resp) => {
        setOrdersLoading(false);
        setOrders(
          resp.data.filter((order: IOrder) => order.status !== "cancelled")
        );
      })
      .catch(() => {
        setOrdersLoading(false);
        notification.error({ message: "Something went wrong." });
      });
  }, []);

  useEffect(() => {
    const filtered = orders.filter((order) => {
      const orderDate = moment(order.created_at);

      const matchesType =
        typeFilter === "all" ? true : order.type === typeFilter;

      const matchesFrom =
        !fromDateFilter ||
        orderDate.isSameOrAfter(moment(fromDateFilter), "day");

      const matchesTo =
        !toDateFilter || orderDate.isSameOrBefore(moment(toDateFilter), "day");

      return matchesType && matchesFrom && matchesTo;
    });

    setFilteredOrders(filtered);
  }, [typeFilter, fromDateFilter, toDateFilter, orders]);

  if (ordersLoading) {
    return <Loader />;
  }

  return (
    <div className="mt-[20px] md:mt-[30px]">
      <div className="mb-[20px] md:mb-[50px] flex items-end w-full">
        <SelectV2
          label="Type"
          options={typeOptions}
          className="max-w-[180px] w-full"
          value={typeOptions.find((x) => x.value === typeFilter)}
          onChange={(opt) => setTypeFilter(opt.value)}
        />

        <div className="ml-[20px] mr-2">
          <div className="text-[16px] normal-case font-semibold mb-4">
            Dates
          </div>
          <DatePicker
            {...defaultDatePickerProps}
            selected={fromDateFilter}
            onChange={setFromDateFilter}
            placeholderText="From"
          />
        </div>

        <div>
          <DatePicker
            {...defaultDatePickerProps}
            selected={toDateFilter}
            onChange={setToDateFilter}
            placeholderText="To"
          />
        </div>
      </div>
      <MyOrdersList refetchOrders={refetchOrders} orders={filteredOrders} />
    </div>
  );
};
